export const formatLatLng = (lat: number, lng: number): string => {
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

export const formatAltitude = (altitude: number): string => {
  return `${altitude.toFixed(1)} m`;
};

/** What a value the aircraft did not report is displayed as (#711). */
export const UNKNOWN_READING = 'Unknown';

export const formatSpeed = (speed: number | undefined): string => {
  if (speed === undefined || !Number.isFinite(speed)) return UNKNOWN_READING;
  return `${speed.toFixed(1)} m/s`;
};

/**
 * A heading as degrees and a compass point. A missing one is "Unknown", never
 * "0° N" (#711): north is a direction, and an aircraft that has not reported
 * one is not pointing north.
 */
export const formatHeading = (heading: number | undefined): string => {
  if (heading === undefined || !Number.isFinite(heading)) return UNKNOWN_READING;
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  // 359.6 rounds to 360, which is north again, not a ninth direction.
  const degrees = Math.round(heading) % 360;
  const index = Math.round(degrees / 45) % 8;
  return `${degrees}° ${directions[index]}`;
};

export const formatDistance = (distanceKm: number): string => {
  if (distanceKm < 1) {
    return `${(distanceKm * 1000).toFixed(0)} m`;
  }
  return `${distanceKm.toFixed(2)} km`;
};

export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

export const formatTimestamp = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    return date.toLocaleString();
  } catch (e) {
    return 'Invalid date';
  }
};

export const formatRelativeTime = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);

    if (diffSecs < 60) {
      return `${diffSecs}s ago`;
    }
    if (diffMins < 60) {
      return `${diffMins}m ago`;
    }
    if (diffHours < 24) {
      return `${diffHours}h ago`;
    }
    return date.toLocaleDateString();
  } catch (e) {
    return 'Unknown';
  }
};

/**
 * A drone's status as words, for a chip or a list row.
 *
 * A table, not the value with its underscores replaced: `idle` is a drone that
 * is switched on and on the ground, which a pilot calls ONLINE (#799, K6), and
 * the API's value stays `idle`.
 */
const STATUS_LABELS: Record<string, string> = {
  idle: 'ONLINE',
  in_flight: 'IN FLIGHT',
  returning: 'RETURNING',
  landed: 'LANDED',
  error: 'ERROR',
  offline: 'OFFLINE',
  link_lost: 'LINK LOST',
  shut_down_in_flight: 'SHUT DOWN IN FLIGHT',
};

export const formatDroneStatus = (status: string): string =>
  STATUS_LABELS[status] ?? status.replace(/_/g, ' ').toUpperCase();
